import { appColors } from '@base/useTheme';
import { TableBorderStyle, ChartSeriesType, ColorBy, Pattern, TableTileColumnVisualization } from '@common/api';
import { Button } from '@components/button';
import { SHARED_AXIS_OPTIONS, getOutliersOptions } from '@components/chart';
import {
    ArrowRight,
    BarChartTable,
    PinChartTable,
    WaterfallChartTable,
    IndicatorChartTable,
    TrafficLightChartTable,
    Close,
    Edit,
    NumberIcon,
    BoldStyle,
    ItalicStyle,
    UnderlineStyle,
} from '@components/icons/FarseerIcons';
import { ColorPicker, ColorSwatchPicker, getTableBorderOptions } from '@features/colors/ColorPicker';
import { FormField, NumberInput, NumberInputField, SelectInput, SwitchField } from '@components/form';
import { GridLayoutIgnoreDrag } from '@components/grid-layout';
import { Divider, DividerVertical } from '@components/layout/Divider';
import { FlexCol, FlexGrow, FlexRow } from '@components/layout/Flex';
import { Popover } from '@components/popover/Popover';
import { ToggleGroup } from '@components/toggle/ToggleGroup';
import { Toggle } from '@components/toggle/Toggle';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Text } from '@components/typography/Typography';
import { FormattingOverride } from '@features/formatting/FormattingOverride';
import { useI18nContext } from '@i18n/i18n-react';
import { $delete, $nop, Update, patch } from '@use-gpu/state';

export const DEFAULT_COLUMN_VISUALIZATION: Required<TableTileColumnVisualization> = {
    color: {
        solid: appColors.ui.dark,
        neutral: appColors.ui.dark,
        colorBy: ColorBy.Static,
        pattern: Pattern.Solid,
    },
    type: ChartSeriesType.None,
    labels: {},
    header: {
        borderBottomColor: appColors.ui.dark,
        borderBottomStyle: TableBorderStyle.Thin,
        domainAxisColor: appColors.neutral.w500,
        domainAxisBorderStyle: TableBorderStyle.Thin,
    },
    spacing: { gridGap: 0 },
    groupOutliers: false,
    groupOutliersRange: 1.5,
    accumulateData: true,
    preserveFormat: false,
    shareAxis: false,
    shareAxisGroup: 1,
};

export const DEFAULT_COLOR_BY: Partial<Record<ChartSeriesType, ColorBy>> = {
    [ChartSeriesType.DataChart]: ColorBy.Value,
    [ChartSeriesType.BarChart]: ColorBy.Value,
    [ChartSeriesType.PinChart]: ColorBy.Value,
    [ChartSeriesType.WaterfallChart]: ColorBy.Trend,
    [ChartSeriesType.IndicatorChart]: ColorBy.Value,
};

export type UpdateColumnVisualization = Update<Record<string, TableTileColumnVisualization> | undefined>;

type TableTileColumnEditProps = {
    align?: 'left' | 'right';
    defaultWidth?: number;
    isGlobalStyle?: boolean;

    columnVisualization?: TableTileColumnVisualization;
    updateColumnVisualization: (u: Update<TableTileColumnVisualization>) => void;
    canVisualize?: boolean;
};

export const TableTileColumnEdit = (props: TableTileColumnEditProps) => {
    const {
        align,
        canVisualize,
        columnVisualization,
        defaultWidth = 0,
        isGlobalStyle,
        updateColumnVisualization,
    } = props;

    const { LL } = useI18nContext();

    const handleReset = () => {
        updateColumnVisualization($delete());
    };

    const canReset = !isGlobalStyle && !!columnVisualization;

    const {
        color,
        type,
        header,
        labels,
        spacing,
        groupOutliers,
        groupOutliersRange,
        preserveFormat,
        accumulateData,
        shareAxis,
        shareAxisGroup,
    } = resolveColumnVisualization(columnVisualization);
    const { columnWidth, gridGap } = spacing;

    const hasChartOrigin =
        type !== ChartSeriesType.None &&
        type !== ChartSeriesType.DataChart &&
        type !== ChartSeriesType.TrafficLightChart &&
        type !== ChartSeriesType.IndicatorChart;
    const hasChartScale = type !== ChartSeriesType.None && type !== ChartSeriesType.DataChart;
    const hasChartColor = type !== ChartSeriesType.None && type !== ChartSeriesType.TrafficLightChart;
    const hasChartPattern = type !== ChartSeriesType.IndicatorChart && type !== ChartSeriesType.TrafficLightChart;
    const isWaterfall = type === ChartSeriesType.WaterfallChart;

    const borderBottomColor = header.borderBottomColor ?? appColors.ui.dark;
    const borderBottomStyle = header.borderBottomStyle ?? TableBorderStyle.Thin;

    const domainAxisColor = header.domainAxisColor ?? appColors.ui.dark;
    const domainAxisBorderStyle = header.domainAxisBorderStyle ?? TableBorderStyle.Thin;

    const labelBold = labels.labelBold ?? false;
    const labelItalic = labels.labelItalic ?? false;
    const labelUnderline = labels.labelUnderline ?? false;

    const labelColor = labels.labelColor ?? appColors.ui.dark;
    const labelFontSize = labels.labelFontSize;
    const labelFormat = labels.labelFormat;

    const sizeEdit = (
        <FlexRow $gap={4}>
            <NumberInputField
                label={LL.WIDTH()}
                size="small"
                value={columnWidth}
                type="int"
                empty={defaultWidth}
                min={0}
                step={1}
                width={90}
                nullable
                upDown
                prefix={<ArrowRight color={appColors.neutral.w700} />}
                suffix={<span>px&nbsp;</span>}
                onChange={(columnWidth) =>
                    updateColumnVisualization({ spacing: { columnWidth: columnWidth ?? $delete() } })
                }
            />
            <NumberInputField
                label={LL.GAP()}
                size="small"
                value={gridGap}
                type="int"
                empty={0}
                min={0}
                step={1}
                width={80}
                upDown
                prefix={<ArrowRight color={appColors.neutral.w700} />}
                suffix={<span>px&nbsp;</span>}
                onChange={(gridGap) => updateColumnVisualization({ spacing: { gridGap } })}
            />
        </FlexRow>
    );

    const columnEdit = (
        <FlexCol $gap={8}>
            {!isGlobalStyle ? (
                <FlexRow $align="center">
                    <Text>{LL.CHART_COLUMN_OPTIONS()}</Text>
                    <FlexGrow />
                    {canReset ? (
                        <Button $type="tertiary" $size="small" title={LL.RESET_STYLES()} onClick={handleReset} />
                    ) : (
                        <div style={{ height: 24 }} />
                    )}
                </FlexRow>
            ) : null}

            {canVisualize ? (
                <FormField label={LL.VISUALIZATION_TYPE()}>
                    <FlexRow $align="center">
                        <ToggleGroup
                            $size="small"
                            value={type}
                            onChange={(type) => {
                                const colorBy = DEFAULT_COLOR_BY[type];
                                updateColumnVisualization({ type, color: { colorBy } });
                            }}
                        >
                            <ToggleGroup.Toggle label={LL.NONE()} icon={Close} value={ChartSeriesType.None} />
                            <ToggleGroup.Toggle
                                label={LL.CONDITIONAL_FORMATTING()}
                                icon={NumberIcon}
                                value={ChartSeriesType.DataChart}
                            />
                            <ToggleGroup.Toggle
                                label={LL.BAR_CHART()}
                                icon={BarChartTable}
                                value={ChartSeriesType.BarChart}
                            />
                            <ToggleGroup.Toggle
                                label={LL.PIN_CHART()}
                                icon={PinChartTable}
                                value={ChartSeriesType.PinChart}
                            />
                            <ToggleGroup.Toggle
                                label={LL.WATERFALL_CHART()}
                                icon={WaterfallChartTable}
                                value={ChartSeriesType.WaterfallChart}
                            />
                            <ToggleGroup.Toggle
                                label={LL.INDICATOR()}
                                icon={IndicatorChartTable}
                                value={ChartSeriesType.IndicatorChart}
                            />
                            <ToggleGroup.Toggle
                                label={LL.TRAFFIC_LIGHT()}
                                icon={TrafficLightChartTable}
                                value={ChartSeriesType.TrafficLightChart}
                            />
                        </ToggleGroup>
                        <FlexGrow />
                        {hasChartColor ? (
                            <ColorSwatchPicker
                                value={color}
                                onChange={(color) =>
                                    updateColumnVisualization({
                                        color,
                                    })
                                }
                                hasBorder={hasChartPattern}
                                hasPattern={hasChartPattern}
                                hasColorBy
                            />
                        ) : null}
                    </FlexRow>
                </FormField>
            ) : null}

            {!isGlobalStyle ? (
                <FormField label={LL.TEXT()}>
                    <FlexRow>
                        <NumberInput
                            type="int"
                            size="small"
                            value={labelFontSize ?? 12}
                            min={6}
                            max={144}
                            step={1}
                            upDown
                            onChange={(labelFontSize) => {
                                updateColumnVisualization({
                                    labels: { labelFontSize },
                                });
                            }}
                            width={60}
                        />

                        <DividerVertical />

                        <TooltipWrapper label={LL.BOLD()}>
                            <Toggle
                                $size="small"
                                icon={BoldStyle}
                                checked={labelBold}
                                onChange={(labelBold) =>
                                    updateColumnVisualization({
                                        labels: { labelBold },
                                    })
                                }
                            />
                        </TooltipWrapper>
                        <TooltipWrapper label={LL.ITALIC()}>
                            <Toggle
                                $size="small"
                                icon={ItalicStyle}
                                checked={labelItalic}
                                onChange={(labelItalic) =>
                                    updateColumnVisualization({
                                        labels: { labelItalic },
                                    })
                                }
                            />
                        </TooltipWrapper>
                        <TooltipWrapper label={LL.UNDERLINED()}>
                            <Toggle
                                $size="small"
                                icon={UnderlineStyle}
                                checked={labelUnderline}
                                onChange={(labelUnderline) =>
                                    updateColumnVisualization({
                                        labels: { labelUnderline },
                                    })
                                }
                            />
                        </TooltipWrapper>

                        <FlexGrow />
                        <ColorPicker
                            value={labelColor}
                            onChange={(labelColor) =>
                                updateColumnVisualization({
                                    labels: { labelColor },
                                })
                            }
                        />
                    </FlexRow>
                </FormField>
            ) : null}

            <FlexCol $gap={16}>
                {!isGlobalStyle ? sizeEdit : null}

                <FlexCol $gap={4}>
                    <FormField label={LL.HEADER()} inline>
                        <FlexRow $gap={8} $align="center">
                            <ToggleGroup
                                $size="small"
                                value={borderBottomStyle}
                                onChange={(borderBottomStyle) =>
                                    updateColumnVisualization({ header: { borderBottomStyle } })
                                }
                            >
                                {getTableBorderOptions(LL).map((option) => (
                                    <ToggleGroup.Toggle
                                        label={option.label}
                                        icon={option.icon}
                                        key={option.value}
                                        value={option.value}
                                    />
                                ))}
                            </ToggleGroup>
                            <ColorPicker
                                value={borderBottomColor}
                                onChange={(borderBottomColor) =>
                                    updateColumnVisualization({
                                        header: { borderBottomColor },
                                    })
                                }
                            />
                        </FlexRow>
                    </FormField>

                    {hasChartOrigin ? (
                        <FormField label={LL.CATEGORY_AXIS()} inline>
                            <FlexRow $gap={8} $align="center">
                                <ToggleGroup
                                    $size="small"
                                    value={domainAxisBorderStyle}
                                    onChange={(domainAxisBorderStyle) => {
                                        updateColumnVisualization({
                                            header: { domainAxisBorderStyle },
                                        });
                                    }}
                                >
                                    {getTableBorderOptions(LL).map((option) => (
                                        <ToggleGroup.Toggle
                                            label={option.label}
                                            icon={option.icon}
                                            key={option.value}
                                            value={option.value}
                                        />
                                    ))}
                                </ToggleGroup>

                                <ColorPicker
                                    value={domainAxisColor}
                                    onChange={(domainAxisColor) =>
                                        updateColumnVisualization({
                                            header: { domainAxisColor },
                                        })
                                    }
                                />
                            </FlexRow>
                        </FormField>
                    ) : null}

                    {isGlobalStyle ? sizeEdit : null}
                </FlexCol>
            </FlexCol>

            {!isGlobalStyle ? (
                <>
                    <Divider />

                    <FlexCol>
                        <FlexCol $gap={8}>
                            {isWaterfall ? (
                                <SwitchField
                                    size="small"
                                    label={LL.ACCUMULATE_DATA()}
                                    value={accumulateData}
                                    onChange={(accumulateData) =>
                                        updateColumnVisualization({
                                            accumulateData,
                                        })
                                    }
                                />
                            ) : null}

                            {hasChartScale ? (
                                <SwitchField
                                    size="small"
                                    label={LL.USE_SHARED_SCALE()}
                                    value={shareAxis}
                                    onChange={(shareAxis) => updateColumnVisualization({ shareAxis })}
                                    suffix={
                                        shareAxis ? (
                                            <div style={{ margin: -1 }}>
                                                <SelectInput
                                                    width={40}
                                                    size="small"
                                                    options={SHARED_AXIS_OPTIONS}
                                                    value={shareAxisGroup}
                                                    onChange={(shareAxisGroup) =>
                                                        updateColumnVisualization({ shareAxisGroup })
                                                    }
                                                />
                                            </div>
                                        ) : null
                                    }
                                />
                            ) : null}

                            <SwitchField
                                size="small"
                                label={LL.GROUP_OUTLIERS()}
                                value={groupOutliers}
                                onChange={(groupOutliers) => updateColumnVisualization({ groupOutliers })}
                                suffix={
                                    groupOutliers ? (
                                        <div style={{ margin: -1 }}>
                                            <SelectInput
                                                width={80}
                                                size="small"
                                                options={getOutliersOptions(LL)}
                                                value={groupOutliersRange}
                                                onChange={(groupOutliersRange) =>
                                                    updateColumnVisualization({ groupOutliersRange })
                                                }
                                            />
                                        </div>
                                    ) : null
                                }
                            />

                            <SwitchField
                                size="small"
                                label={LL.PRESERVE_FORMATTING()}
                                value={preserveFormat}
                                onChange={(preserveFormat) => updateColumnVisualization({ preserveFormat })}
                            />

                            <FormattingOverride
                                size="small"
                                numberFormat={!preserveFormat ? labelFormat ?? null : null}
                                onChange={(labelFormat) => {
                                    updateColumnVisualization({
                                        labels: { labelFormat: labelFormat ?? $delete() },
                                        preserveFormat: labelFormat ? false : $nop(),
                                    });
                                }}
                            />
                        </FlexCol>
                    </FlexCol>
                </>
            ) : null}
        </FlexCol>
    );

    if (isGlobalStyle) return <div>{columnEdit}</div>;

    return (
        <>
            <Popover.Trigger anchor={false}>
                <div style={{ transform: align === 'right' ? 'scale(-1, 1)' : undefined }}>
                    <Edit color={labelColor} />
                </div>
            </Popover.Trigger>
            <Popover.Content width={260} $padding="7px" $small noFocus>
                <GridLayoutIgnoreDrag>
                    <div style={{ padding: 1 }} onClick={(e) => e.stopPropagation()}>
                        {columnEdit}
                    </div>
                </GridLayoutIgnoreDrag>
            </Popover.Content>
        </>
    );
};

export const resolveColumnVisualization = (
    columnVisualization?: TableTileColumnVisualization,
    defaultVisualization: Required<TableTileColumnVisualization> = DEFAULT_COLUMN_VISUALIZATION
): Required<TableTileColumnVisualization> =>
    patch(defaultVisualization, columnVisualization) as Required<TableTileColumnVisualization>;
